/**
 * End-to-End (E2E) Binary Hybrid Cryptographic Engine for Browser
 * Implements RSA-OAEP (SHA-256) + AES-256-GCM using native Web Crypto API (window.crypto.subtle)
 */

const PROTOCOL_MAGIC_BYTE = 0x99;
const AES_GCM_NONCE_LENGTH = 12;
const AES_GCM_TAG_LENGTH = 16;

let cachedServerPublicKey = null;
let cachedPublicKeyPem = null;
let keyFetchPromise = null;

/**
 * Convert Base64 string to Uint8Array / ArrayBuffer
 * @param {string} base64 
 * @returns {ArrayBuffer}
 */
function base64ToArrayBuffer(base64) {
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Import SPKI RSA Public Key PEM into Web Crypto API Key
 * @param {string} pem 
 * @returns {Promise<CryptoKey>}
 */
export async function importPublicKey(pem) {
  const cleanPem = pem
    .replace(/-----BEGIN PUBLIC KEY-----/g, '')
    .replace(/-----END PUBLIC KEY-----/g, '')
    .replace(/[\r\n\s]/g, '');

  const spkiBuffer = base64ToArrayBuffer(cleanPem);

  return await window.crypto.subtle.importKey(
    'spki',
    spkiBuffer,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['encrypt']
  );
}

/**
 * Fetch and cache the Server's RSA Public Key from the API
 * @param {string} [baseUrl] 
 * @returns {Promise<CryptoKey>}
 */
export async function getServerPublicKey(baseUrl = 'http://localhost:4017/api') {
  if (cachedServerPublicKey) {
    return cachedServerPublicKey;
  }

  if (keyFetchPromise) {
    return await keyFetchPromise;
  }

  keyFetchPromise = (async () => {
    try {
      const response = await fetch(`${baseUrl}/auth/public-key`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch public key: HTTP ${response.status}`);
      }

      const result = await response.json();
      const pubPem = result?.body?.publicKey || result?.publicKey;

      if (!pubPem) {
        throw new Error('Public key missing in server response');
      }

      cachedPublicKeyPem = pubPem;
      cachedServerPublicKey = await importPublicKey(pubPem);
      return cachedServerPublicKey;
    } catch (err) {
      console.warn('⚠️ Could not fetch E2E public key from server:', err.message);
      throw err;
    } finally {
      keyFetchPromise = null;
    }
  })();

  return await keyFetchPromise;
}

/**
 * Encrypt any JavaScript payload and pack into a binary E2E request frame
 * Frame Format:
 * [1 Byte Magic (0x99)] + [2 Bytes KeyLen (K)] + [K Bytes Encrypted AES Key] + [12 Bytes Nonce] + [16 Bytes Tag] + [N Bytes Ciphertext]
 * 
 * @param {any} data - Object or primitive
 * @param {string} [baseUrl]
 * @returns {Promise<{ binaryBuffer: ArrayBuffer, rawSessionKey: Uint8Array, cryptoSessionKey: CryptoKey }>}
 */
export async function encryptAndPackRequest(data, baseUrl) {
  const publicKey = await getServerPublicKey(baseUrl);

  // 1. Generate 256-bit (32-byte) random session key
  const rawSessionKey = window.crypto.getRandomValues(new Uint8Array(32));
  const cryptoSessionKey = await window.crypto.subtle.importKey(
    'raw',
    rawSessionKey,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );

  // 2. Generate 12-byte random nonce
  const nonce = window.crypto.getRandomValues(new Uint8Array(AES_GCM_NONCE_LENGTH));

  // 3. Encrypt payload with anti-replay envelope using AES-256-GCM
  const envelope = {
    _e2e_ts: Date.now(),
    _e2e_nonce: Math.random().toString(36).substring(2) + Date.now().toString(36),
    payload: data,
  };
  const jsonStr = JSON.stringify(envelope);
  const textEncoder = new TextEncoder();
  const plainBytes = textEncoder.encode(jsonStr);

  // Web Crypto AES-GCM output format: [Ciphertext (N bytes) | Auth Tag (16 bytes)]
  const gcmOutputBuffer = await window.crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: nonce,
      tagLength: 128,
    },
    cryptoSessionKey,
    plainBytes
  );

  const gcmBytes = new Uint8Array(gcmOutputBuffer);
  const ciphertextLength = gcmBytes.length - AES_GCM_TAG_LENGTH;
  const ciphertext = gcmBytes.subarray(0, ciphertextLength);
  const authTag = gcmBytes.subarray(ciphertextLength);

  // 4. Encrypt session key with Server Public Key (RSA-OAEP-SHA256)
  const encryptedKeyBuffer = await window.crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    publicKey,
    rawSessionKey
  );
  const encryptedKeyBytes = new Uint8Array(encryptedKeyBuffer);
  const keyLength = encryptedKeyBytes.length; // 256 bytes for RSA-2048

  // 5. Pack everything into binary request frame
  const totalLength = 1 + 2 + keyLength + AES_GCM_NONCE_LENGTH + AES_GCM_TAG_LENGTH + ciphertextLength;
  const frame = new Uint8Array(totalLength);

  let offset = 0;
  frame[offset] = PROTOCOL_MAGIC_BYTE;
  offset += 1;

  // Key length (Uint16 Big-Endian)
  frame[offset] = (keyLength >> 8) & 0xff;
  frame[offset + 1] = keyLength & 0xff;
  offset += 2;

  // Encrypted Session Key
  frame.set(encryptedKeyBytes, offset);
  offset += keyLength;

  // Nonce
  frame.set(nonce, offset);
  offset += AES_GCM_NONCE_LENGTH;

  // Auth Tag
  frame.set(authTag, offset);
  offset += AES_GCM_TAG_LENGTH;

  // Ciphertext
  frame.set(ciphertext, offset);

  return {
    binaryBuffer: frame.buffer,
    rawSessionKey,
    cryptoSessionKey,
  };
}

/**
 * Unpack and decrypt a binary E2E response frame
 * Response Frame Format:
 * [1 Byte Magic (0x99)] + [12 Bytes Nonce] + [16 Bytes Tag] + [N Bytes Ciphertext]
 * 
 * @param {ArrayBuffer} arrayBuffer 
 * @param {CryptoKey} cryptoSessionKey 
 * @returns {Promise<any>}
 */
export async function unpackAndDecryptResponse(arrayBuffer, cryptoSessionKey) {
  if (!arrayBuffer || arrayBuffer.byteLength < 1 + AES_GCM_NONCE_LENGTH + AES_GCM_TAG_LENGTH) {
    return null;
  }

  const bytes = new Uint8Array(arrayBuffer);
  const magic = bytes[0];
  if (magic !== PROTOCOL_MAGIC_BYTE) {
    return null;
  }

  let offset = 1;
  const nonce = bytes.subarray(offset, offset + AES_GCM_NONCE_LENGTH);
  offset += AES_GCM_NONCE_LENGTH;

  const authTag = bytes.subarray(offset, offset + AES_GCM_TAG_LENGTH);
  offset += AES_GCM_TAG_LENGTH;

  const ciphertext = bytes.subarray(offset);

  // Reconstruct Web Crypto GCM input: [Ciphertext | Auth Tag]
  const gcmInput = new Uint8Array(ciphertext.length + authTag.length);
  gcmInput.set(ciphertext, 0);
  gcmInput.set(authTag, ciphertext.length);

  // Decrypt with AES-256-GCM
  const decryptedBuffer = await window.crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: nonce,
      tagLength: 128,
    },
    cryptoSessionKey,
    gcmInput
  );

  const textDecoder = new TextDecoder();
  const text = textDecoder.decode(decryptedBuffer);

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Generate an ephemeral AES-GCM session key and encrypt it with Server RSA Public Key for header transmission
 * @param {string} [baseUrl] 
 * @returns {Promise<{ base64EncryptedKey: string, cryptoSessionKey: CryptoKey }>}
 */
export async function createEncryptedSessionHeader(baseUrl) {
  const publicKey = await getServerPublicKey(baseUrl);

  // 1. Generate 256-bit (32-byte) random session key
  const rawSessionKey = window.crypto.getRandomValues(new Uint8Array(32));
  const cryptoSessionKey = await window.crypto.subtle.importKey(
    'raw',
    rawSessionKey,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );

  // 2. Encrypt session key with Server Public Key (RSA-OAEP-SHA256)
  const encryptedKeyBuffer = await window.crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    publicKey,
    rawSessionKey
  );

  const bytes = new Uint8Array(encryptedKeyBuffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64EncryptedKey = window.btoa(binary);

  return {
    base64EncryptedKey,
    cryptoSessionKey,
  };
}

export default {
  getServerPublicKey,
  encryptAndPackRequest,
  unpackAndDecryptResponse,
  createEncryptedSessionHeader,
  importPublicKey,
};
